import React from "react";
import { motion } from "framer-motion";
import { Database, Computer, Terminal, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";

const Main = () => {
  const navigate = useNavigate();

  const cards = [
    {
      key: "analysis",
      badge: "Origenomi Analysis",
      Icon: Computer,
      accent: {
        iconBg: "bg-emerald-100",
        iconText: "text-emerald-700",
        badgeText: "text-emerald-700",
        card:
          "bg-gradient-to-br from-emerald-50 via-white to-emerald-100/70 border border-emerald-100",
        blobA: "bg-emerald-200/40 -top-10 -left-12",
        blobB: "bg-emerald-100/30 bottom-0 right-[-60px]",
        button: "bg-emerald-700 hover:bg-emerald-800",
      },
      title: (
        <>
          Decoding Bacterial{" "}
          <span className="text-emerald-700">Replication Origins</span>
        </>
      ),
      description: (
        <>
          Harness the power of <strong>OriGenomi</strong> — a research-driven
          engine built to predict <strong>OriC</strong> and{" "}
          <strong>DnaA</strong> regions from bacterial genomes with unmatched
          accuracy. Upload your genome or use RefSeq IDs for instant origin
          visualization and insights.
        </>
      ),
      cta: "Start Genome Analysis",
      onClick: () => navigate("/search"),
      delay: 0.1,
    },
    {
      key: "database",
      badge: "Genome Database",
      Icon: Database,
      accent: {
        iconBg: "bg-violet-100",
        iconText: "text-violet-700",
        badgeText: "text-violet-700",
        card:
          "bg-gradient-to-br from-violet-50 via-white to-violet-100/70 border border-violet-100",
        blobA: "bg-violet-200/35 -top-12 right-0",
        blobB: "bg-violet-100/30 bottom-[-40px] left-[-40px]",
        button: "bg-violet-700 hover:bg-violet-800",
      },
      title: (
        <>
          Explore the OriC &amp; DnaA{" "}
          <span className="text-violet-700">Genome Database</span>
        </>
      ),
      description: (
        <>
          Access over <strong>60,000 bacterial genomes</strong> annotated with{" "}
          <strong>OriC</strong> and <strong>DnaA</strong> coordinates. Browse,
          compare, and download curated genomic data for replication and origin
          studies.
        </>
      ),
      cta: "Browse Database",
      onClick: () => navigate("/database"),
      delay: 0.2,
    },
    {
      key: "cli",
      badge: "CLI Tool",
      Icon: Terminal,
      accent: {
        iconBg: "bg-violet-100",
        iconText: "text-violet-700",
        badgeText: "text-violet-700",
        card:
          "bg-gradient-to-br from-violet-50 via-white to-violet-100/70 border border-violet-100",
        blobA: "bg-violet-200/35 -top-10 right-[-40px]",
        blobB: "bg-violet-100/30 bottom-[-48px] left-[-48px]",
        button: "bg-violet-700 hover:bg-violet-800",
      },
      title: (
        <>
          Install &amp; Run the{" "}
          <span className="text-violet-700">Origenomi CLI</span>
        </>
      ),
      description: (
        <>
          Prefer the command line? Set up <strong>Origenomi</strong> locally to
          run OriC/DnaA prediction, trimming, and genome rotation in your own
          workflows.
        </>
      ),
      cta: "CLI Installation",
      onClick: () => navigate("/cli"),
      delay: 0.3,
    },
    {
      key: "about",
      badge: "About / Manual",
      Icon: BookOpen,
      accent: {
        iconBg: "bg-emerald-100",
        iconText: "text-emerald-700",
        badgeText: "text-emerald-700",
        card:
          "bg-gradient-to-br from-emerald-50 via-white to-emerald-100/70 border border-emerald-100",
        blobA: "bg-emerald-200/40 -top-12 -left-10",
        blobB: "bg-emerald-100/30 bottom-[-40px] right-[-40px]",
        button: "bg-emerald-700 hover:bg-emerald-800",
      },
      title: (
        <>
          Learn the{" "}
          <span className="text-emerald-700">Workflow &amp; Interpretation</span>
        </>
      ),
      description: (
        <>
          New here? Read the manual to understand input formats, what OriC/DnaA
          predictions mean, and how to interpret the visualizations and reports.
        </>
      ),
      cta: "Open Manual",
      onClick: () => navigate("/about"),
      delay: 0.4,
    },
  ];

  return (
    <div className="bg-gradient-to-b from-white via-slate-50 to-slate-100 font-sans">
      <motion.section
        className="max-w-7xl mx-auto px-6 py-16 md:py-24"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {cards.map(({ key, badge, Icon, accent, title, description, cta, onClick, delay }) => (
            <motion.div
              key={key}
              className={`relative overflow-hidden rounded-2xl p-8 shadow-md ${accent.card}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay }}
            >
              <div
                className={`absolute w-48 h-48 rounded-full blur-3xl ${accent.blobA}`}
              />
              <div
                className={`absolute w-56 h-56 rounded-full blur-3xl ${accent.blobB}`}
              />

              <div className="relative z-10 flex flex-col gap-6 text-center items-center">
                <div className="flex items-center gap-2">
                  <div className={`${accent.iconBg} p-3 rounded-full shadow-sm`}>
                    <Icon className={accent.iconText} size={26} />
                  </div>
                  <span
                    className={`uppercase ${accent.badgeText} font-semibold text-sm tracking-wide`}
                  >
                    {badge}
                  </span>
                </div>

                <div className="space-y-4">
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-800 leading-tight">
                    {title}
                  </h2>
                  <p className="text-gray-600 text-[15px] leading-relaxed">
                    {description}
                  </p>
                </div>

                <div>
                  <button
                    onClick={onClick}
                    className={`${accent.button} text-white px-7 py-3 rounded-md text-sm md:text-[15px] font-medium transition-all shadow-md hover:shadow-lg`}
                  >
                    {cta}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      <Footer />
    </div>
  );
};

export default Main;
